const US_STATE_NAMES: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

export function formatUsStateName(stateCode: string | null | undefined): string | null {
  const normalized = stateCode?.trim().toUpperCase();
  if (!normalized) return null;
  return US_STATE_NAMES[normalized] ?? normalized;
}

export type UpcomingLawDisplayName = {
  primary: string;
  secondary: string | null;
};

/** Humanize Indiana-style enrolled act IDs such as HB1000.06.ENRS. */
export function formatUpcomingLawDisplayName(rawName: string): UpcomingLawDisplayName {
  const trimmed = rawName.trim();
  const enrolledMatch = trimmed.match(/^(HB|SB)(\d+)(?:\.\d+)?\.ENRS$/i);
  if (enrolledMatch) {
    const chamber = enrolledMatch[1].toUpperCase() === "HB" ? "House Bill" : "Senate Bill";
    return {
      primary: `${chamber} ${enrolledMatch[2]}`,
      secondary: null,
    };
  }

  const billMatch = trimmed.match(/^(HB|SB)(\d+)/i);
  if (billMatch) {
    const chamber = billMatch[1].toUpperCase() === "HB" ? "House Bill" : "Senate Bill";
    return {
      primary: `${chamber} ${billMatch[2]}`,
      secondary: trimmed !== `${billMatch[1]}${billMatch[2]}` ? trimmed : null,
    };
  }

  return { primary: trimmed, secondary: null };
}

export function legislationMonitorSubtitle(municipalityName: string, stateCode: string | null): string {
  const stateName = formatUsStateName(stateCode);
  if (stateName) {
    return `Upcoming ${stateName} laws taking effect for ${municipalityName}`;
  }
  return `Legislation Monitor for ${municipalityName}`;
}

/** Empty-list copy for Legislation Monitor when there is nothing to show. */
export function legislationMonitorEmptyListMessage(options: {
  sessionStates: string[];
  municipalityName: string | null | undefined;
  municipalityState: string | null | undefined;
  selectedState: string | null;
  query: string;
}): string {
  if (options.sessionStates.length === 0) {
    return "No states are configured for Legislation Monitor on your account.";
  }
  if (!options.municipalityName) {
    return "Select a municipality to view upcoming laws.";
  }
  if (options.selectedState == null) {
    const stateName = formatUsStateName(options.municipalityState);
    if (stateName) {
      return `Legislation Monitor is not available for ${options.municipalityName} (${stateName}) on your account.`;
    }
    return `Legislation Monitor is not available for ${options.municipalityName} on your account.`;
  }
  if (options.query.trim()) {
    return "No upcoming laws match your search.";
  }
  return `No upcoming laws found for ${formatUsStateName(options.selectedState)}.`;
}
